import { Layout } from "@layouts";
import { Item, airdropConfirmationColumns, PageBreadcrumbs } from "@molecules";
import { Bar, DataTable } from "@organisms";
import { Button as SButton, Progress } from "@shadcn";
import { useLocation, useNavigate } from "react-router";
import { useContext, useEffect, useState } from "react";
import { GiftCardOffer, PagePaths, AirdropPagePaths } from "../../../../types";
import { CurrencyTypes, PremiumGiftCardAirdropRequest } from "@api";
import { AuthContext, useUser } from "@auth";
import ReactModal from "react-modal";
import { Button, IconVariantType } from "@atoms";
import { styles as globalStyles } from "../../../../styles/styles";
import { useTranslation } from "react-i18next";

export type AirdropConfirmPageTemplateProps = {
  progress: number;
  onReturnPressed: () => void;
  airdropGiftCard: (
    airdropGiftCardRequest: PremiumGiftCardAirdropRequest,
    paymentId: string,
  ) => Promise<void>;
};

export const AirdropConfirmPageTemplate = ({
  progress,
  airdropGiftCard,
}: AirdropConfirmPageTemplateProps) => {
  const { billingDetails, isLoadingData, setIsLoadingData, partner, setSelectedPageIndex } =
    useContext(AuthContext);
  const { t } = useTranslation();
  const [isAttemptingAirdropGiftCard, setIsAttemptingAirdropGiftCard] =
    useState(false);
  const [airdropCompleted, setIsAirdropCompleted] = useState(false);
  const [billingInformationFound, setBillingInformationFound] = useState(billingDetails !== null && billingDetails !== undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const goToPerks = () => {
    setSelectedPageIndex?.(3);
    navigate(`/dashboard/${PagePaths.perks}`);
  };
  const goToAirdrops = () => {
    setSelectedPageIndex?.(3);
    navigate(`/dashboard/${PagePaths.airdrop}/main`);
  };
  const assembledData = location.state?.assembledData;
  const combinedData = assembledData?.combinedData;
  const selectedUser = combinedData?.selectedUser;
  const offerSelected = combinedData?.offerSelected;
  const amount = assembledData?.amount;
  const message = assembledData?.message;

  const goToSelectMembers = () => {
    setSelectedPageIndex?.(3);
    navigate(`/dashboard/${PagePaths.airdrop}/${AirdropPagePaths.selectMembers}`);
  };

  const goToSelectGiftCard = () => {
    if (selectedUser) {
      setSelectedPageIndex?.(3);
      navigate(`/dashboard/${PagePaths.airdrop}/${AirdropPagePaths.selectOffers}` , {
        state: { selectedUser },
      });
    } else {
      setSelectedPageIndex?.(3);
      navigate(`/dashboard/${PagePaths.airdrop}/${AirdropPagePaths.selectOffers}`);
    }
  };

  const goToSelectAmount = () => {
    if (offerSelected && selectedUser) {
      setSelectedPageIndex?.(3);
      navigate(`/dashboard/${PagePaths.airdrop}/${AirdropPagePaths.selectAmount}`, {
        state: { combinedData: { offerSelected, selectedUser } },
      });
    } else {
      setSelectedPageIndex?.(3);
      navigate(`/dashboard/${PagePaths.airdrop}/${AirdropPagePaths.selectAmount}`);
    }
  };
  const [creditCards, setCreditCards] = useState<boolean>(billingDetails?.creditCards?.length === undefined ? false : billingDetails?.creditCards?.length > 0);
  const { TeamTable } = DataTable({
    columns: airdropConfirmationColumns,
    data: [selectedUser],
    searchable: false,
  });

  useEffect(() => {
    if (!combinedData) {
      navigate(`/dashboard/${PagePaths.airdrop}/${AirdropPagePaths.selectMembers}`);
    }
  }, [combinedData, navigate]);

  console.log("message: ", message);

  if (!combinedData || !selectedUser || !offerSelected) {
    return null;
  }

  useEffect(() => {
    billingDetails?.creditCards ? setBillingInformationFound(true) : setBillingInformationFound(false)
  }, [billingDetails])

  const onReturnPressed = () => {
    navigate(-1)
  }

  const onIsAirdropAttempted = () => {
    setIsAttemptingAirdropGiftCard(true);
  };

  const closeAirdropModal = () => {
    setIsAttemptingAirdropGiftCard(false);
    setIsAirdropCompleted(false);
  };

  const onPressAirdrop = async () => {
    setIsLoadingData(true);
    try {
      const premiumGiftCardAirdropRequest: PremiumGiftCardAirdropRequest = {
        giftCardOfferObjectId: offerSelected?.id,
        amount,
        network: offerSelected?.networkName,
        currency: CurrencyTypes.CAD,
        premiumUserId: selectedUser?.userId,
        partnerName: partner?.organization ?? "",
        partnerReceipt: undefined,
        giftCardAirdropMessage: message,
      };
      await airdropGiftCard(
        premiumGiftCardAirdropRequest,
        billingDetails?.creditCards?.[0]?.paymentId ?? "",
      )
      setIsAirdropCompleted(true);
      return;
    } catch (error) {
      console.log("Failed Airdrop: " + JSON.stringify(error));
    }
    
  };
  return (
    <Layout
      variant="dashboard"
      header={t("airdrop.common.header")}
      subheader={
        <div className="flex flex-row items-start justify-between max-w-5xl">
          <div className="page-subtitle mt-2">
            {t("airdrop.common.subheader")}
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        <PageBreadcrumbs
          items={[
            { label: t("airdrop.common.breadcrumbs.benefits"), onClick: goToPerks },
            { label: t("airdrop.common.breadcrumbs.airdrops"), onClick: goToAirdrops },
            { label: t("airdrop.common.breadcrumbs.selectMembers"), onClick: goToSelectMembers },
            { label: t("airdrop.common.breadcrumbs.selectGiftCard"), onClick: goToSelectGiftCard },
            { label: t("airdrop.common.breadcrumbs.selectAmount"), onClick: goToSelectAmount },
            { label: t("airdrop.common.breadcrumbs.confirm") },
          ]}
        />
        <div className="flex flex-col gap-6 border border-neutral-light bg-white rounded-2xl w-full min-w-[400px] p-6">
          <div className="">
            <h2 className="text-xl font-normal">
              <div className="flex items-center gap-3 leading-none p-[5px]">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-white border border-neutral-light text-2xl leading-none">
                  4.
                </span>
                <span className="flex items-center gap-2 leading-none">
                  {t("airdrop.confirm.title")}
                </span>
              </div>
            </h2>
          </div>
          <div className="flex flex-row justify-start p-0 max-w-5xl bg-white rounded-md mb-16">
            <div className="rounded-2xl my-2 ml-2 mr-5 min-w-max border border-neutral-medium2 bg-white p-6">
              <img
                src={offerSelected?.images?.[0]?.imageUrl}
                className="rounded-xl h-48 w-80 object-cover"
              />
            </div>
            <div className="flex flex-col justify-between m-2 pb-2 rounded-md">
              <div className="text-2xl font-normal">
                {t("airdrop.confirm.summary", {
                  amount: `$${amount}`,
                  brand: offerSelected?.brandName,
                  name: `${selectedUser?.firstName} ${selectedUser?.lastName}`,
                })}
              </div>
              <div className="text-lg pt-2">
                {message ? t("airdrop.confirm.messageIncluded", { message }) : t("airdrop.confirm.messageMissing")}
              </div>
              <div className="">
                <div className="color-neutral-dark text-md pb-4 pt-2">
                  {t("airdrop.confirm.paymentLine", {
                    email: selectedUser?.emailAddress,
                    cardBrand: billingDetails?.creditCards?.[0]?.cardBrand,
                    last4: billingDetails?.creditCards?.[0]?.lastFourDigits,
                  })}
                </div>
                <div className="text-neutral-dark text-sm">
                  {t("airdrop.confirm.notification", {
                    name: selectedUser?.firstName,
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Bar
          variant="bottom"
          onReturnPressed={onReturnPressed}
          onContinuePressed={onIsAirdropAttempted}
          isContinueDisabled={!billingInformationFound}
          altContinueText={t("airdrop.confirm.sendAirdrop")}
        />

        {/* confirm modal */}
        {isAttemptingAirdropGiftCard ? (
          <ReactModal
            isOpen={isAttemptingAirdropGiftCard}
            onRequestClose={closeAirdropModal}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
          >
            <div>
              <div className="flex flex-col items-center py-4">
                <div className="font-semibold text-2xl flex flex-row justify-center pb-2">
                  {t("airdrop.confirm.modal.title")}
                </div>
                <div className="mb-8 flex flex-row justify-center w-60 text-center">
                  {t("airdrop.confirm.modal.description", {
                    amount: `$${amount}`,
                    brand: offerSelected?.brandName,
                    name: selectedUser?.firstName,
                  })}
                </div>
                <div className="flex flex-row justify-center">
                  <div className="w-64">
                    <Button
                      onClick={
                        isLoadingData
                          ? () => {}
                          : onPressAirdrop
                      }
                      variant="primary"
                      label={t("airdrop.confirm.modal.confirm")}
                      // withIcon={"rocket"}
                      stretch
                      textWeight={"semibold"}
                      color="primary-medium"
                      loading={isLoadingData}
                    />
                  </div>
                </div>
              </div>
            </div>
          </ReactModal>
        ) : null}
      </div>
    </Layout>
  );
};
